import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ClickableText } from '../ClickableWord';
import './AnnotatedChunks.css';

// ── Types ────────────────────────────────────────────────────

export interface SpeakingAnnotation {
  word: string;
  type: 'stress' | 'link' | 'pause' | 'reduction' | 'ipa' | 'syllables';
  hint?: string;
}

export interface AnnotatedChunk {
  text: string;
  meaning?: string;
  annotations?: SpeakingAnnotation[];
  intonation?: 'rising' | 'falling' | null;
  pause?: boolean;
}

interface AnnotatedChunksProps {
  chunks: AnnotatedChunk[];
  tone?: string;
  speakingTip?: string;
  /** Show chunk meanings below text (default: true) */
  showMeaning?: boolean;
  /** Use ClickableText for word lookup (default: false) */
  clickable?: boolean;
}

// ── Tone translation (top 40 by frequency) ──────────────────

const TONE_KEYS = [
  'Informative', 'Formal', 'Encouraging', 'Professional', 'Friendly',
  'Reassuring', 'Inquisitive', 'Helpful', 'Neutral', 'Polite',
  'Enthusiastic', 'Instructive', 'Serious', 'Appreciative', 'Academic',
  'Concerned', 'Excited', 'Instructional', 'Confident', 'Reflective',
  'Curious', 'Empathetic', 'Sincere', 'Warm', 'Analytical',
  'Welcoming', 'Direct', 'Grateful', 'Decisive', 'Thoughtful',
  'Authoritative', 'Firm', 'Narrative', 'Casual', 'Surprised',
  'Inquiring', 'Interested', 'Cheerful', 'Determined', 'Relieved',
] as const;

function translateTone(tone: string, t: (key: string) => string): string {
  const key = tone.trim();
  if (TONE_KEYS.includes(key as any)) {
    const translated = t(`shadowing.tones.${key}`);
    // Fallback to raw English if key missing
    if (translated !== `shadowing.tones.${key}`) return translated;
  }
  return tone;
}

// ── Annotation rendering helpers ────────────────────────────

interface TextSegment {
  text: string;
  type?: 'stress' | 'link' | 'pause' | 'reduction' | 'ipa' | 'syllables';
  hint?: string;
}

/**
 * Split chunk text into segments based on annotations.
 */
function buildSegments(text: string, annotations: SpeakingAnnotation[]): TextSegment[] {
  if (!annotations || annotations.length === 0) {
    return [{ text }];
  }

  const segments: TextSegment[] = [];

  const sorted = [...annotations]
    .map(a => ({ ...a, idx: text.indexOf(a.word) }))
    .filter(a => a.idx >= 0)
    .sort((a, b) => a.idx - b.idx);

  let offset = 0;
  for (const ann of sorted) {
    const pos = text.indexOf(ann.word, offset);
    if (pos < 0) continue;

    if (pos > offset) {
      segments.push({ text: text.slice(offset, pos) });
    }

    segments.push({
      text: ann.word,
      type: ann.type,
      hint: ann.hint,
    });

    offset = pos + ann.word.length;
  }

  if (offset < text.length) {
    segments.push({ text: text.slice(offset) });
  }

  return segments;
}

/**
 * Render syllable hint with stressed parts highlighted.
 * e.g. "uh-MEHR-ih-kuhn" → "uh-" + <strong>MEHR</strong> + "-ih-kuhn"
 */
function renderSyllableHint(hint: string): React.ReactNode {
  // Split on hyphens but keep them as separators
  const parts = hint.split(/(-)/);
  return parts.map((part, i) => {
    if (part === '-') return <React.Fragment key={i}>-</React.Fragment>;
    // Uppercase parts are stressed
    if (part === part.toUpperCase() && /[A-Z]/.test(part)) {
      return <strong key={i} className="ac-hint-stress">{part}</strong>;
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}

/**
 * Render a single annotated segment with tooltip on hover.
 * hintIndex: running counter for hints with text, used to alternate above/below.
 * Returns [ReactNode, newHintIndex].
 */
function renderSegment(
  seg: TextSegment,
  i: number,
  hintIndex: number,
  annotationLabels: Record<string, string>,
): [React.ReactNode, number] {
  if (!seg.type) {
    return [<React.Fragment key={i}>{seg.text}</React.Fragment>, hintIndex];
  }

  const hasHint = seg.hint && (seg.type === 'syllables' || seg.type === 'reduction' || seg.type === 'ipa');
  const posClass = hasHint ? (hintIndex % 2 === 0 ? 'ac-hint--below' : 'ac-hint--above') : '';
  const nextIdx = hasHint ? hintIndex + 1 : hintIndex;
  const tooltip = annotationLabels[seg.type] || '';

  switch (seg.type) {
    case 'stress':
      return [<span key={i} className="ac-stress" title={tooltip}>{seg.text}</span>, nextIdx];
    case 'link':
      return [<span key={i} className="ac-link" title={tooltip}>{seg.text}</span>, nextIdx];
    case 'pause':
      return [(
        <React.Fragment key={i}>
          <span className="ac-pause">{seg.text}</span>
          <span className="ac-pause-mark" title={tooltip}>‖</span>
        </React.Fragment>
      ), nextIdx];
    case 'syllables':
      return [(
        <span key={i} className="ac-annotated-word" title={tooltip}>
          <span className="ac-syllables">{seg.text}</span>
          {seg.hint && <span className={`ac-hint ac-hint--syllables ${posClass}`}>{renderSyllableHint(seg.hint)}</span>}
        </span>
      ), nextIdx];
    case 'reduction':
      return [(
        <span key={i} className="ac-annotated-word" title={tooltip}>
          <span className="ac-reduction">{seg.text}</span>
          {seg.hint && <span className={`ac-hint ac-hint--reduction ${posClass}`}>→ {seg.hint}</span>}
        </span>
      ), nextIdx];
    case 'ipa':
      return [(
        <span key={i} className="ac-annotated-word" title={tooltip}>
          <span className="ac-ipa">{seg.text}</span>
          {seg.hint && <span className={`ac-hint ac-hint--ipa ${posClass}`}>{seg.hint}</span>}
        </span>
      ), nextIdx];
    default:
      return [<React.Fragment key={i}>{seg.text}</React.Fragment>, nextIdx];
  }
}

// ── Legend help icon with popover / modal ────────────────────

const LegendHelp: React.FC<{ t: (key: string) => string }> = ({ t }) => {
  const [open, setOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsMobile(window.innerWidth < 640);
  }, []);

  // Close popover on outside click
  useEffect(() => {
    if (!open || isMobile) return;
    const handler = (e: MouseEvent) => {
      if (
        popoverRef.current && !popoverRef.current.contains(e.target as Node) &&
        btnRef.current && !btnRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, isMobile]);

  const legendContent = (
    <div className="ac-legend-content">
      <span className="ac-legend-item"><span className="ac-stress">bold</span> {t('shadowing.annotations.stress')}</span>
      <span className="ac-legend-item"><span className="ac-link-demo">wavy</span> {t('shadowing.annotations.link')}</span>
      <span className="ac-legend-item"><span className="ac-pause-mark">‖</span> {t('shadowing.annotations.pause')}</span>
      <span className="ac-legend-item"><span className="ac-syllables-demo">dash</span> {t('shadowing.annotations.syllables')}</span>
      <span className="ac-legend-item"><span className="ac-reduction-demo">dot</span> {t('shadowing.annotations.reduction')}</span>
      <span className="ac-legend-item"><span className="ac-ipa-demo">line</span> {t('shadowing.annotations.ipa')}</span>
      <span className="ac-legend-item">↗↘ {t('shadowing.annotations.intonation')}</span>
    </div>
  );

  return (
    <span className="ac-legend-help-wrap">
      <button
        ref={btnRef}
        className="ac-legend-help-btn"
        onClick={() => setOpen(v => !v)}
        type="button"
        aria-label={t('shadowing.annotations.legendToggleShow')}
      >
        ?
      </button>

      {/* Desktop: popover */}
      {open && !isMobile && (
        <div ref={popoverRef} className="ac-legend-popover">
          {legendContent}
        </div>
      )}

      {/* Mobile: modal */}
      {open && isMobile && (
        <div className="ac-legend-modal-overlay" onClick={() => setOpen(false)}>
          <div className="ac-legend-modal" onClick={e => e.stopPropagation()}>
            <div className="ac-legend-modal-header">
              <span>{t('shadowing.annotations.legendToggleShow')}</span>
              <button className="ac-legend-modal-close" onClick={() => setOpen(false)}>✕</button>
            </div>
            {legendContent}
          </div>
        </div>
      )}
    </span>
  );
};

// ── Main component ──────────────────────────────────────────

export const AnnotatedChunks: React.FC<AnnotatedChunksProps> = ({
  chunks,
  tone,
  speakingTip,
  showMeaning = true,
  clickable = false,
}) => {
  const { t } = useTranslation('learning');

  const hasAnnotations = chunks.some(
    c => (c.annotations && c.annotations.length > 0) || c.intonation || c.pause
  );

  // Build annotation label map for tooltips
  const annotationLabels: Record<string, string> = {
    stress: t('shadowing.annotations.stress'),
    link: t('shadowing.annotations.link'),
    pause: t('shadowing.annotations.pause'),
    syllables: t('shadowing.annotations.syllables'),
    reduction: t('shadowing.annotations.reduction'),
    ipa: t('shadowing.annotations.ipa'),
  };

  return (
    <div className="annotated-chunks">
      {/* Tone label (i18n'd) */}
      {tone && (
        <span className="ac-tone-label">{translateTone(tone, t)}</span>
      )}

      {/* ① Full sentence — with annotations when available, plain text otherwise */}
      <div className="ac-sentence">
        {(() => {
          let hintIdx = 0;
          return chunks.map((chunk, idx) => {
            const segments = buildSegments(chunk.text, chunk.annotations || []);
            const rendered: React.ReactNode[] = [];
            for (let si = 0; si < segments.length; si++) {
              const [node, newIdx] = renderSegment(segments[si], si, hintIdx, annotationLabels);
              rendered.push(node);
              hintIdx = newIdx;
            }
            return (
              <span key={idx} className="ac-chunk-inline">
                {rendered}
                {chunk.intonation === 'rising' && (
                  <span className="ac-intonation rising" title={t('shadowing.annotations.intonation')}>↗</span>
                )}
                {chunk.intonation === 'falling' && (
                  <span className="ac-intonation falling" title={t('shadowing.annotations.intonation')}>↘</span>
                )}
                {chunk.pause && <span className="ac-pause-mark" title={t('shadowing.annotations.pause')}>‖</span>}
                {' '}
              </span>
            );
          });
        })()}
      </div>

      {/* ② Chunk breakdown list with meanings */}
      {showMeaning && (
        <div className="ac-chunks-list">
          {chunks.map((chunk, idx) => (
            <div key={idx} className="ac-chunk-item">
              <span className="ac-chunk-text">
                {clickable ? <ClickableText text={chunk.text} /> : chunk.text}
              </span>
              {chunk.meaning && (
                <span className="ac-chunk-meaning">{chunk.meaning}</span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Speaking tip */}
      {speakingTip && (
        <div className="ac-speaking-tip">
          <strong>💡</strong> {speakingTip}
        </div>
      )}

      {/* Legend help icon (? button) — replaces old collapsible toggle */}
      {hasAnnotations && <LegendHelp t={t} />}
    </div>
  );
};

export default AnnotatedChunks;
